// src/utils/referralPointsFixer.js
// Browser-based utility to investigate and fix missing referral points
// Usage: Call window.fixReferralPoints() from browser console when logged in as admin

import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";

import {
  REFERRAL_COMMISSION_MAX_LEVELS,
  REFERRAL_COMMISSION_RATES,
} from "../config/referralCommissionConfig";

const COMMISSION_RATES = REFERRAL_COMMISSION_RATES;
const MAX_LEVELS = REFERRAL_COMMISSION_MAX_LEVELS;

// Get referral chain for a user
async function getUserReferralChain(userId) {
  try {
    const referralChain = [];
    let currentUserId = userId;
    let level = 1;

    while (level <= MAX_LEVELS && currentUserId) {
      const referralQuery = query(
        collection(db, "referrals"),
        where("referredUserId", "==", currentUserId)
      );
      const referralSnapshot = await getDocs(referralQuery);

      if (referralSnapshot.empty) break;

      const referralData = referralSnapshot.docs[0].data();
      const referrerId = referralData.referrerId;

      const referrerQuery = query(
        collection(db, "users"),
        where("uid", "==", referrerId)
      );
      const referrerSnapshot = await getDocs(referrerQuery);

      if (referrerSnapshot.empty) break;

      const referrerData = referrerSnapshot.docs[0].data();

      referralChain.push({
        level,
        referrerId,
        referrerName: referrerData.name,
        referrerEmail: referrerData.email,
        hasPurchasedPackage: referrerData.hasPurchasedPackage || false,
        commissionRate: COMMISSION_RATES[level] || 0,
        userDocId: referrerSnapshot.docs[0].id,
      });

      currentUserId = referrerId;
      level++;
    }

    return referralChain;
  } catch (error) {
    console.error("Error getting referral chain:", error);
    return [];
  }
}

// Main investigation function
export async function investigateReferralPoints(referrerEmails) {
  console.log("=".repeat(80));
  console.log("🔍 INVESTIGATING MISSING REFERRAL POINTS");
  console.log("=".repeat(80));
  console.log("Referrers to check:", referrerEmails.join(", "));

  const missingCommissions = [];
  const summary = {
    referrersChecked: 0,
    ordersChecked: 0,
    ordersWithMissingCommissions: 0,
    totalMissingAmount: 0,
  };

  for (const referrerEmail of referrerEmails) {
    summary.referrersChecked++;
    console.log("\n" + "=".repeat(60));
    console.log(`📧 CHECKING REFERRER: ${referrerEmail}`);
    console.log("=".repeat(60));

    // Get referrer user data
    const referrerQuery = query(
      collection(db, "users"),
      where("email", "==", referrerEmail)
    );
    const referrerSnapshot = await getDocs(referrerQuery);

    if (referrerSnapshot.empty) {
      console.log(`❌ Referrer ${referrerEmail} not found`);
      continue;
    }

    const referrerData = referrerSnapshot.docs[0].data();
    const referrerId = referrerData.uid;
    const referrerDocId = referrerSnapshot.docs[0].id;

    console.log(`\n📋 Referrer Details:`);
    console.log(`   - UID: ${referrerId}`);
    console.log(`   - Name: ${referrerData.name}`);
    console.log(`   - hasPurchasedPackage: ${referrerData.hasPurchasedPackage}`);
    console.log(`   - confirmedCommissions: ₹${referrerData.confirmedCommissions || 0}`);
    console.log(`   - commissionBalance: ₹${referrerData.commissionBalance || 0}`);
    console.log(`   - commissionEarned: ₹${referrerData.commissionEarned || 0}`);

    if (!referrerData.hasPurchasedPackage) {
      console.log(`\n⚠️  WARNING: Referrer has NOT purchased a package - NOT eligible for commissions`);
      continue;
    }

    // Find users referred by this referrer
    const referralsQuery = query(
      collection(db, "referrals"),
      where("referrerId", "==", referrerId)
    );
    const referralsSnapshot = await getDocs(referralsQuery);

    if (referralsSnapshot.empty) {
      console.log(`\n❌ No referrals found for ${referrerEmail}`);
      continue;
    }

    console.log(`\n📊 Found ${referralsSnapshot.size} referrals made by ${referrerEmail}`);

    // Get orders from referred users
    for (const referralDoc of referralsSnapshot.docs) {
      const referralData = referralDoc.data();
      const referredUserId = referralData.referredUserId;

      console.log(`\n   🔗 Referred User: ${referralData.referredUserName || referredUserId}`);

      // Get referred user's confirmed orders
      const ordersQuery = query(
        collection(db, "cartOrders"),
        where("buyerId", "==", referredUserId),
        where("orderStatus", "==", "confirmed")
      );
      const ordersSnapshot = await getDocs(ordersQuery);

      if (ordersSnapshot.empty) {
        console.log(`      ⏳ No confirmed orders yet`);
        continue;
      }

      console.log(`      📦 Found ${ordersSnapshot.size} confirmed orders`);

      // Check if commissions exist for each order
      for (const orderDoc of ordersSnapshot.docs) {
        summary.ordersChecked++;
        const orderData = orderDoc.data();
        const orderId = orderData.cartOrderId;

        console.log(`\n         📝 Order: ${orderId}`);
        console.log(`            - Confirmed At: ${orderData.confirmedAt}`);
        console.log(`            - Total: ₹${orderData.grandTotal || orderData.totalAmount}`);

        // Check existing commissions for this order and referrer
        const commissionsQuery = query(
          collection(db, "commissions"),
          where("orderId", "==", orderId),
          where("referrerId", "==", referrerId)
        );
        const commissionsSnapshot = await getDocs(commissionsQuery);

        if (!commissionsSnapshot.empty) {
          let totalExisting = 0;
          commissionsSnapshot.docs.forEach((d) => {
            totalExisting += d.data().commissionAmount || 0;
          });
          console.log(`            ✅ Existing commissions: ${commissionsSnapshot.size} records, Total: ₹${totalExisting.toFixed(2)}`);
          continue;
        }

        console.log(`            ❌ NO COMMISSIONS FOUND!`);
        summary.ordersWithMissingCommissions++;

        // Calculate what commissions should exist
        let totalPotentialCommission = 0;
        const itemsToProcess = [];
        const packagesToProcess = [];

        // Check items
        if (orderData.items && orderData.items.length > 0) {
          console.log(`            📋 Items in order: ${orderData.items.length}`);
          for (const item of orderData.items) {
            const hasReferralShare = item.referralShare === true;
            const profitMargin = (item.profitMargin || 0) * (item.quantity || 1);
            
            console.log(`               - ${item.name}: referralShare=${hasReferralShare}, profitMargin=₹${profitMargin}`);
            
            if (hasReferralShare && profitMargin > 0) {
              const commission = (profitMargin * COMMISSION_RATES[1]) / 100;
              totalPotentialCommission += commission;
              itemsToProcess.push({
                ...item,
                totalProfitMargin: profitMargin,
                potentialCommission: commission,
              });
              console.log(`                 ✅ Will create commission: ₹${commission.toFixed(2)}`);
            }
          }
        }

        // Check packages - Activation packages do NOT share referral points (only items do)
        if (orderData.packages && orderData.packages.length > 0) {
          console.log(`            📋 Packages in order: ${orderData.packages.length} (⚠️ Packages don't share referral points)`);
          for (const pkg of orderData.packages) {
            console.log(`               - ${pkg.name}: ₹${pkg.price} (no commission - activation packages exempt)`);
          }
        }

        // If no items or packages found at all, log it
        if ((!orderData.items || orderData.items.length === 0) && (!orderData.packages || orderData.packages.length === 0)) {
          console.log(`            ⚠️ Order has no items or packages!`);
        }

        if (totalPotentialCommission > 0) {
          console.log(`\n            💰 MISSING COMMISSION (Level 1): ₹${totalPotentialCommission.toFixed(2)}`);
          summary.totalMissingAmount += totalPotentialCommission;

          missingCommissions.push({
            referrerId,
            referrerEmail,
            referrerName: referrerData.name,
            referrerDocId,
            orderId,
            orderData,
            buyerId: referredUserId,
            buyerName: orderData.buyerName,
            buyerEmail: orderData.buyerEmail,
            items: itemsToProcess,
            packages: packagesToProcess,
            totalPotentialCommission,
            level: 1,
          });
        }
      }
    }
  }

  console.log("\n\n" + "=".repeat(80));
  console.log("📊 INVESTIGATION SUMMARY");
  console.log("=".repeat(80));
  console.log(`Referrers Checked: ${summary.referrersChecked}`);
  console.log(`Orders Checked: ${summary.ordersChecked}`);
  console.log(`Orders with Missing Commissions: ${summary.ordersWithMissingCommissions}`);
  console.log(`Total Missing Amount: ₹${summary.totalMissingAmount.toFixed(2)}`);

  return { missingCommissions, summary };
}

// Fix missing commissions
export async function fixMissingCommissions(missingCommissions) {
  console.log("\n\n" + "=".repeat(80));
  console.log("🔧 FIXING MISSING COMMISSIONS");
  console.log("=".repeat(80));

  if (missingCommissions.length === 0) {
    console.log("✅ No missing commissions to fix.");
    return { success: true, fixed: 0, totalAmount: 0 };
  }

  let totalFixed = 0;
  let commissionsCreated = 0;
  const userUpdates = {};

  for (const missing of missingCommissions) {
    console.log(`\n📝 Processing Order: ${missing.orderId}`);
    console.log(`   - Referrer: ${missing.referrerEmail} (Level ${missing.level})`);
    console.log(`   - Buyer: ${missing.buyerEmail}`);

    // Process items
    for (const item of missing.items) {
      const commissionId = `MLCOMM-FIX-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const commissionAmount = Math.round(item.potentialCommission * 100) / 100;

      const commissionRecord = {
        commissionId,
        type: "item_multilevel_referral",
        orderId: missing.orderId,
        orderType: "item",

        referrerId: missing.referrerId,
        referrerName: missing.referrerName,
        referrerEmail: missing.referrerEmail,

        buyerId: missing.buyerId,
        buyerName: missing.buyerName,
        buyerEmail: missing.buyerEmail,

        itemId: item.id,
        itemName: item.name,
        itemQuantity: item.quantity || 1,

        commissionLevel: missing.level,
        commissionPercentage: COMMISSION_RATES[missing.level],
        commissionAmount: commissionAmount,
        profitMargin: item.totalProfitMargin,
        orderAmount: (item.sellingPrice || item.price) * (item.quantity || 1),

        status: "confirmed",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        confirmedAt: new Date().toISOString(),

        eligibilityChecked: true,
        referrerPackagePurchaseStatus: true,
        isMultiLevelCommission: true,
        isRetroactiveFix: true,
        fixDate: new Date().toISOString(),
        notes: `Retroactive fix - Level ${missing.level} referral points (${COMMISSION_RATES[missing.level]}% of profit margin)`,
      };

      await addDoc(collection(db, "commissions"), commissionRecord);
      console.log(`   ✅ Created commission for item "${item.name}": ₹${commissionAmount}`);

      totalFixed += commissionAmount;
      commissionsCreated++;

      // Track user updates
      if (!userUpdates[missing.referrerId]) {
        userUpdates[missing.referrerId] = {
          docId: missing.referrerDocId,
          email: missing.referrerEmail,
          amount: 0,
        };
      }
      userUpdates[missing.referrerId].amount += commissionAmount;
    }

    // Note: Packages (activation fees) do NOT generate referral commissions - only items do
  }

  // Update user balances
  console.log("\n📊 Updating user balances...");
  for (const [userId, update] of Object.entries(userUpdates)) {
    const userDocRef = doc(db, "users", update.docId);

    // Get current values
    const userQuery = query(collection(db, "users"), where("uid", "==", userId));
    const userSnapshot = await getDocs(userQuery);

    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data();
      const currentBalance = userData.commissionBalance || 0;
      const currentEarned = userData.commissionEarned || 0;
      const currentConfirmed = userData.confirmedCommissions || 0;
      const currentTotal = userData.totalCommissions || 0;

      await updateDoc(userDocRef, {
        commissionBalance: currentBalance + update.amount,
        commissionEarned: currentEarned + update.amount,
        confirmedCommissions: currentConfirmed + update.amount,
        totalCommissions: currentTotal + update.amount,
        updatedAt: new Date().toISOString(),
      });

      console.log(`   ✅ Updated ${update.email}: ₹${currentBalance.toFixed(2)} → ₹${(currentBalance + update.amount).toFixed(2)}`);
    }
  }

  console.log("\n" + "=".repeat(80));
  console.log("✅ FIX COMPLETE");
  console.log("=".repeat(80));
  console.log(`Commissions Created: ${commissionsCreated}`);
  console.log(`Total Amount Fixed: ₹${totalFixed.toFixed(2)}`);

  return { success: true, fixed: commissionsCreated, totalAmount: totalFixed };
}

// Main function to call from browser console
export async function fixReferralPointsForEmails(referrerEmails) {
  console.log("🚀 Starting Referral Points Fix Tool...");
  console.log("Emails to check:", referrerEmails);

  // Investigate
  const { missingCommissions, summary } = await investigateReferralPoints(referrerEmails);

  if (missingCommissions.length === 0) {
    console.log("\n✅ No missing commissions found!");
    return { investigation: summary, fix: null };
  }

  console.log("\n⚠️  Found missing commissions. Fixing now...");

  // Fix
  const fixResult = await fixMissingCommissions(missingCommissions);

  return { investigation: summary, fix: fixResult };
}

// Attach to window for browser console access
if (typeof window !== "undefined") {
  window.fixReferralPoints = async () => {
    const emails = ["onlineworld1987@gmail.com", "infinitywhale08@gmail.com"];
    return await fixReferralPointsForEmails(emails);
  };

  window.investigateReferralPoints = async (emails) => {
    if (!emails) {
      emails = ["onlineworld1987@gmail.com", "infinitywhale08@gmail.com"];
    }
    return await investigateReferralPoints(emails);
  };

  console.log("🔧 Referral Points Fixer loaded!");
  console.log("   - Run window.investigateReferralPoints() to check for issues");
  console.log("   - Run window.fixReferralPoints() to fix missing commissions");
}

export default { investigateReferralPoints, fixMissingCommissions, fixReferralPointsForEmails };

